import { db } from '../config';
import { collection, addDoc, updateDoc, deleteDoc, doc, getDocs, query, where, Timestamp } from 'firebase/firestore';
import { upsertSlip } from './slipService';

export interface Contract {
  id?: string;
  slipId: string;
  slipNumber: string;
  customerId: string;
  customerName: string;
  startDate: Date;
  endDate: Date;
  status: 'active' | 'pending' | 'expired' | 'cancelled';
  paymentStatus: 'paid' | 'pending' | 'overdue';
  amount: number;
  documents: string[];
  paymentReceivedDate?: Date;
  paymentMethod?: 'cash' | 'credit_card' | 'check' | 'bank_transfer' | 'square' | 'other';
  paymentNotes?: string;
  createdAt: Date;
  updatedAt: Date;
}

const COLLECTION_NAME = 'contracts';

export const createContract = async (contractData: Omit<Contract, 'id' | 'createdAt' | 'updatedAt'>): Promise<Contract> => {
  try {
    const now = new Date();
    const contractWithTimestamps = {
      ...contractData,
      createdAt: now,
      updatedAt: now,
      startDate: Timestamp.fromDate(contractData.startDate),
      endDate: Timestamp.fromDate(contractData.endDate),
      paymentReceivedDate: contractData.paymentReceivedDate ? Timestamp.fromDate(contractData.paymentReceivedDate) : null,
    };

    const docRef = await addDoc(collection(db, COLLECTION_NAME), contractWithTimestamps);
    
    // Update the slip's status and payment status
    const slipRef = doc(db, 'slips', contractData.slipId);
    await updateDoc(slipRef, {
      contractId: docRef.id,
      paymentStatus: contractData.paymentStatus,
      status: contractData.status === 'active' ? 'occupied' : 'reserved',
      updatedAt: Timestamp.fromDate(now)
    });

    return {
      ...contractData,
      id: docRef.id,
      createdAt: now,
      updatedAt: now,
    };
  } catch (error) {
    console.error('Error creating contract:', error);
    throw error;
  }
};

export const updateContract = async (contractId: string, contractData: Partial<Contract>): Promise<void> => {
  try {
    const contractRef = doc(db, COLLECTION_NAME, contractId);
    const updateData: any = {
      ...contractData,
      updatedAt: Timestamp.fromDate(new Date()),
    };

    // Convert Date objects to Timestamps
    if (contractData.startDate) {
      updateData.startDate = Timestamp.fromDate(contractData.startDate);
    }
    if (contractData.endDate) {
      updateData.endDate = Timestamp.fromDate(contractData.endDate);
    }
    if (contractData.paymentReceivedDate) {
      updateData.paymentReceivedDate = Timestamp.fromDate(contractData.paymentReceivedDate);
    }

    await updateDoc(contractRef, updateData);

    // Update the slip's status and payment status if they were changed
    if (contractData.slipId) {
      const slipRef = doc(db, 'slips', contractData.slipId);
      const slipUpdateData: any = {
        contractId,
        updatedAt: Timestamp.fromDate(new Date())
      };

      if (contractData.paymentStatus) {
        slipUpdateData.paymentStatus = contractData.paymentStatus;
      }
      if (contractData.status) {
        slipUpdateData.status = contractData.status === 'active' ? 'occupied' : 'reserved';
      }

      await updateDoc(slipRef, slipUpdateData);
    }
  } catch (error) {
    console.error('Error updating contract:', error);
    throw error;
  }
};

export const deleteContract = async (contractId: string): Promise<void> => {
  try {
    // Get the contract to find the associated slip
    const contractRef = doc(db, COLLECTION_NAME, contractId);
    const contractDoc = await getDocs(query(collection(db, COLLECTION_NAME), where('id', '==', contractId)));
    const contractData = contractDoc.docs[0]?.data();

    // Delete the contract
    await deleteDoc(contractRef);

    // Update the slip to remove contract reference
    if (contractData?.slipId) {
      const slipRef = doc(db, 'slips', contractData.slipId);
      await updateDoc(slipRef, {
        contractId: null,
        paymentStatus: null,
        status: 'available',
        updatedAt: Timestamp.fromDate(new Date())
      });
    }
  } catch (error) {
    console.error('Error deleting contract:', error);
    throw error;
  }
};

export const getAllContracts = async (): Promise<Contract[]> => {
  try {
    const querySnapshot = await getDocs(collection(db, COLLECTION_NAME));
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        startDate: data.startDate.toDate(),
        endDate: data.endDate.toDate(),
        paymentReceivedDate: data.paymentReceivedDate?.toDate(),
        createdAt: data.createdAt.toDate(),
        updatedAt: data.updatedAt.toDate(),
      } as Contract;
    });
  } catch (error) {
    console.error('Error getting contracts:', error);
    throw error;
  }
};

export const getContractsByCustomer = async (customerId: string): Promise<Contract[]> => {
  try {
    const q = query(collection(db, COLLECTION_NAME), where('customerId', '==', customerId));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        startDate: data.startDate.toDate(),
        endDate: data.endDate.toDate(),
        paymentReceivedDate: data.paymentReceivedDate?.toDate(),
        createdAt: data.createdAt.toDate(),
        updatedAt: data.updatedAt.toDate(),
      } as Contract;
    });
  } catch (error) {
    console.error('Error getting customer contracts:', error);
    throw error;
  }
};

export const getContractsBySlip = async (slipId: string): Promise<Contract[]> => {
  try {
    const q = query(collection(db, COLLECTION_NAME), where('slipId', '==', slipId));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        startDate: data.startDate.toDate(),
        endDate: data.endDate.toDate(),
        paymentReceivedDate: data.paymentReceivedDate?.toDate(),
        createdAt: data.createdAt.toDate(),
        updatedAt: data.updatedAt.toDate(),
      } as Contract;
    });
  } catch (error) {
    console.error('Error getting slip contracts:', error);
    throw error;
  }
}; 